import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  type EngagementParts,
  type EngagementSummary,
  type DeliverableVersionRecord,
  type FolderListing,
  type StreamOutput,
  type TeamMember,
  type WorkStream,
  type WorkTask,
  type WorkView,
} from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  Button,
  Empty,
  Failed,
  Field,
  Mark,
  Select,
  SkeletonRows,
  TextArea,
  ClassificationSelect,
} from "../components/primitives";
import { day, stamp } from "../lib/format";
import { useSession } from "../lib/session";
import { RaidRegister } from "./Raid";

/**
 * Work — the engagement's workstreams, run rather than listed.
 *
 * **What a workstream actually is.** Researched rather than assumed: in
 * consulting practice a workstream is a parallel stream of work scoped
 * around *one decision or hypothesis*, owned by a named lead, carrying its
 * own milestones and deliverables, and reported as a single weekly line —
 * on track or not, the next milestone, what it needs from another stream.
 *
 * The first version of this screen had none of that. A workstream was a
 * name with tasks under it, which is a to-do list grouped by heading. So
 * migration 030 gave the table the three things a person states and nothing
 * can derive — `lead_user_id`, `intent`, `health` + `health_note` — and
 * this screen puts them where they belong: at the head of the stream, as
 * the status line.
 *
 * **Deliverables were already there.** `deliverable.workstream_id` has
 * existed since migration 002 and nothing had ever read it, so the outputs
 * a stream exists to produce were invisible on the one screen about
 * producing them.
 *
 * **Health is a judgement, not a calculation.** Counting overdue tasks
 * gives a number; only the lead knows whether the stream is in trouble.
 * Same reasoning as the official progress figure on the Desk.
 *
 * **Authority** comes from the server per row — `mayManage` for the board,
 * `mayEdit` and `mayMove` per task. An assignee moves their own task and
 * cannot rewrite it; migration 031 enforces that in a trigger after the
 * test suite caught the policy being wider than its own comment claimed.
 */
export function Work() {
  const { id } = useParams<{ id: string }>();
  const engagementId = id ?? "";
  const { me } = useSession();

  const work = useResource<WorkView>(
    () => api.get<WorkView>(`/api/engagements/${engagementId}/work`),
    [engagementId],
  );
  const engagements = useResource<EngagementSummary[]>(
    () => api.get<EngagementSummary[]>("/api/engagements"),
    [],
  );
  const parts = useResource<EngagementParts>(
    () => api.get<EngagementParts>(`/api/engagements/${engagementId}/parts`),
    [engagementId],
  );
  const team = useResource<TeamMember[]>(
    () => api.get<TeamMember[]>(`/api/engagements/${engagementId}/team`),
    [engagementId],
  );
  // A deliverable version is a file already in the engagement's drive, so
  // submitting one is choosing from Files rather than a second upload path.
  const folders = useResource<FolderListing[]>(
    () => api.get<FolderListing[]>(`/api/engagements/${engagementId}/folders`),
    [engagementId],
  );

  const [mineOnly, setMineOnly] = useState(false);
  const [showDone, setShowDone] = useState(false);

  const engagement =
    engagements.resource.state === "ready"
      ? engagements.resource.data.find((e) => e.id === engagementId)
      : undefined;

  const view = work.resource.state === "ready" ? work.resource.data : undefined;

  /**
   * Filtering happens here, once, so the counts in each stream's header
   * describe what you are actually looking at. Filtering inside each
   * stream would leave the headers describing a board you cannot see.
   */
  const filtered = useMemo(() => {
    if (!view) return undefined;
    const keep = (t: WorkTask) =>
      (!mineOnly || t.assigneeId === me?.userId) &&
      (showDone || t.status !== "done");
    return {
      ...view,
      workstreams: view.workstreams.map((w) => ({
        ...w,
        tasks: w.tasks.filter(keep),
      })),
      unfiled: view.unfiled.filter(keep),
    };
  }, [view, mineOnly, showDone, me?.userId]);

  const totals = useMemo(() => {
    if (!view) return { all: 0, mine: 0, done: 0 };
    const all = [...view.unfiled, ...view.workstreams.flatMap((w) => w.tasks)];
    return {
      all: all.length,
      mine: all.filter((t) => t.assigneeId === me?.userId).length,
      done: all.filter((t) => t.status === "done").length,
    };
  }, [view, me?.userId]);

  /** Every document in the engagement, flattened for a single select. */
  const files = useMemo(() => {
    if (folders.resource.state !== "ready") return [];
    return folders.resource.data.flatMap((f) =>
      f.documents
        .filter((d) => d.latestVersionId)
        .map((d) => ({
          versionId: d.latestVersionId!,
          label: `${d.name}`,
          folder: f.folderKey,
        })),
    );
  }, [folders.resource]);

  const assignable = useMemo(
    () =>
      team.resource.state === "ready"
        ? team.resource.data.filter(
            (m) =>
              m.kind !== "client" &&
              (m.status === "accepted" || m.status === "active"),
          )
        : [],
    [team.resource],
  );

  return (
    <AppShell
      engagement={
        engagement && {
          reference: engagement.reference,
          name: engagement.name,
          organisation: engagement.organisation,
        }
      }
    >
      <ScreenHead title="Work" />

      {work.resource.state === "loading" && <SkeletonRows rows={5} />}
      {work.resource.state === "failed" && (
        <Failed
          message={work.resource.message}
          correlationId={work.resource.correlationId}
        />
      )}

      {filtered && view && (
        <>
          {totals.all > 0 && (
            <Filters
              mineOnly={mineOnly}
              showDone={showDone}
              totals={totals}
              onMine={() => setMineOnly((v) => !v)}
              onDone={() => setShowDone((v) => !v)}
            />
          )}

          {view.workstreams.length === 0 && view.unfiled.length === 0 && (
            <Empty
              title="Nothing to do yet"
              action={
                view.mayManage ? (
                  <Link
                    to={`/engagements/${engagementId}/setup`}
                    className="inline-flex min-h-11 items-center justify-center border border-ink bg-ink px-5 text-sm font-medium text-canvas transition-colors duration-200 hover:bg-ink/85"
                  >
                    Add a workstream
                  </Link>
                ) : undefined
              }
            >
              {view.mayManage
                ? "A workstream groups work around one clear question or outcome. Create one in Setup, then manage it here."
                : "Nobody has put any work on this engagement yet."}
            </Empty>
          )}

          {filtered.workstreams.map((stream) => (
            <Stream
              key={stream.id}
              engagementId={engagementId}
              stream={stream}
              mayManage={view.mayManage}
              parts={parts.resource.state === "ready" ? parts.resource.data : undefined}
              assignable={assignable}
              files={files}
              filtered={mineOnly || !showDone}
              onChanged={work.reload}
            />
          ))}

          {filtered.unfiled.length > 0 && (
            <Stream
              engagementId={engagementId}
              stream={{
                id: "",
                name: "Not in a workstream",
                sequence: 0,
                intent: null,
                leadUserId: null,
                lead: null,
                health: "on_track",
                healthNote: null,
                nextMilestone: null,
                outputs: [],
                tasks: filtered.unfiled,
              }}
              loose
              mayManage={view.mayManage}
              parts={parts.resource.state === "ready" ? parts.resource.data : undefined}
              assignable={assignable}
              files={files}
              filtered={mineOnly || !showDone}
              onChanged={work.reload}
            />
          )}

          {/* The register sits under the streams rather than beside them:
              in a weekly review it is read as one list, and each entry
              still names the stream it belongs to. */}
          <RaidRegister
            engagementId={engagementId}
            parts={parts.resource.state === "ready" ? parts.resource.data : undefined}
            assignable={assignable}
          />
        </>
      )}
    </AppShell>
  );
}

function Filters({
  mineOnly,
  showDone,
  totals,
  onMine,
  onDone,
}: {
  mineOnly: boolean;
  showDone: boolean;
  totals: { all: number; mine: number; done: number };
  onMine: () => void;
  onDone: () => void;
}) {
  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-line pb-4 text-sm">
      <Toggle on={mineOnly} onClick={onMine}>
        Only mine <span className="text-muted">({totals.mine})</span>
      </Toggle>
      <Toggle on={showDone} onClick={onDone}>
        Show done <span className="text-muted">({totals.done})</span>
      </Toggle>
      <span className="text-secondary">{totals.all} in total</span>
    </div>
  );
}

function Toggle({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={
        "border-b-2 pb-0.5 transition-colors duration-150 " +
        (on
          ? "border-ink text-ink"
          : "border-transparent text-secondary hover:text-ink")
      }
    >
      {children}
    </button>
  );
}

const HEALTH: Record<string, { label: string; tone: "live" | "warning" | "danger" }> = {
  on_track: { label: "On track", tone: "live" },
  at_risk: { label: "At risk", tone: "warning" },
  off_track: { label: "Off track", tone: "danger" },
};

/** One workstream: its status line, what it must produce, and the work. */
function Stream({
  engagementId,
  stream,
  loose,
  mayManage,
  parts,
  assignable,
  files,
  filtered,
  onChanged,
}: {
  engagementId: string;
  stream: WorkStream;
  loose?: boolean;
  mayManage: boolean;
  parts?: EngagementParts;
  assignable: TeamMember[];
  files: FileChoice[];
  /** Whether any filter is narrowing the board, so empty can say why. */
  filtered: boolean;
  onChanged: () => void;
}) {
  const ceiling = parts?.ceiling ?? "client_visible";
  const [adding, setAdding] = useState(false);
  const [editingStream, setEditingStream] = useState(false);
  const [addingOutput, setAddingOutput] = useState(false);

  const open = stream.tasks.filter((t) => t.status !== "done").length;
  const overdue = stream.tasks.filter((t) => t.overdue).length;
  const health = HEALTH[stream.health] ?? HEALTH.on_track!;

  return (
    <section className="mt-10">
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-line pb-3">
        <h2 className={`text-lg ${loose ? "text-secondary" : "text-ink"}`}>
          {stream.name}
        </h2>
        <div className="flex flex-wrap items-center gap-4 text-sm text-secondary">
          {!loose && <Mark tone={health.tone}>{health.label}</Mark>}
          <span>
            {stream.tasks.length === 0
              ? "Nothing here"
              : `${open} open of ${stream.tasks.length}`}
            {overdue > 0 && <span className="text-danger"> · {overdue} overdue</span>}
          </span>
          {!loose && mayManage && (
            <Button tone="quiet" onClick={() => setEditingStream((v) => !v)}>
              {editingStream ? "Close" : "Edit stream"}
            </Button>
          )}
        </div>
      </div>

      {/* The status line a consulting stream is reported by: what question
          it answers, who owns it, what is next, and what it needs. */}
      {!loose && (
        <StatusLine stream={stream} mayManage={mayManage} onChanged={onChanged} />
      )}

      {editingStream && !loose && (
        <EditStream
          stream={stream}
          assignable={assignable}
          onDone={() => {
            setEditingStream(false);
            onChanged();
          }}
          onCancel={() => setEditingStream(false)}
        />
      )}

      {!loose && (
        <section className="mt-5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <span className="label">Produces</span>
            {/* Not gated on mayManage, and that was my mistake. The policy
                is `deliverable_insert` from 004 — reach > 0, within the
                ceiling, not a client — and `deliverable` is pointedly
                absent from migration 010's list of tables reserved to
                app.may_manage. Consultants produce the work; Leads decide
                on it. I even have a test asserting exactly that, written
                this morning, and then hid the button from them anyway. */}
            {!addingOutput && (
              <Button tone="quiet" onClick={() => setAddingOutput(true)}>
                Add a deliverable
              </Button>
            )}
          </div>

          {stream.outputs.length === 0 && !addingOutput && (
            <p className="mt-2 max-w-[62ch] text-sm text-secondary">
              Nothing yet. Add the deck, model, recommendation or other result
              this workstream needs to produce, then submit updated versions as
              the work progresses.
            </p>
          )}

          {stream.outputs.map((d) => (
            <Deliverable
              key={d.id}
              deliverable={d}
              engagementId={engagementId}
              mayManage={mayManage}
              files={files}
              onChanged={onChanged}
            />
          ))}

          {addingOutput && (
            <AddDeliverable
              engagementId={engagementId}
              workstreamId={stream.id}
              ceiling={ceiling}
              onDone={() => {
                setAddingOutput(false);
                onChanged();
              }}
              onCancel={() => setAddingOutput(false)}
            />
          )}
        </section>
      )}

      <div className="mt-4">
        {/* "Nothing matching here" was shown whether or not a filter was
            on, which reads as a dead end when nothing is filtered. Say
            which it is. */}
        {stream.tasks.length === 0 && !adding && (
          <p className="max-w-[62ch] text-sm text-secondary">
            {filtered
              ? "No tasks here match the filters above."
              : mayManage
                ? "No tasks in this workstream yet."
                : "No tasks in this workstream yet. A Lead adds them; you will see yours here and can move them as you go."}
          </p>
        )}

        {stream.tasks.map((t) => (
          <TaskRow
            key={t.id}
            task={t}
            parts={parts}
            assignable={assignable}
            onChanged={onChanged}
          />
        ))}
      </div>

      {/* Only a manager can create a task — task_insert asks may_manage. */}
      {mayManage && !adding && (
        <Button tone="quiet" onClick={() => setAdding(true)}>
          Add a task
        </Button>
      )}

      {mayManage && adding && (
        <AddTask
          engagementId={engagementId}
          workstreamId={loose ? null : stream.id}
          parts={parts}
          assignable={assignable}
          onDone={() => {
            setAdding(false);
            onChanged();
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </section>
  );
}

function StatusLine({
  stream,
  mayManage,
  onChanged,
}: {
  stream: WorkStream;
  mayManage: boolean;
  onChanged: () => void;
}) {
  const completeIt = useAction(async (milestoneId: string) => {
    await api.post(`/api/milestones/${milestoneId}/complete`, {});
    onChanged();
  });

  const nothingSaid =
    !stream.intent && !stream.lead && !stream.nextMilestone && !stream.healthNote;

  if (nothingSaid) return null;

  return (
    <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
      {stream.intent && (
        <p className="max-w-[62ch] text-ink sm:col-span-2">{stream.intent}</p>
      )}
      {stream.lead && (
        <p className="text-secondary">
          <span className="label">Led by</span> {stream.lead}
        </p>
      )}
      {stream.nextMilestone && (
        <p className="text-secondary">
          <span className="label">Next</span> {stream.nextMilestone.name}
          {stream.nextMilestone.dueOn && ` · ${day(stream.nextMilestone.dueOn)}`}
          {/* `completeMilestone` has been in the domain layer since Slice
              1 with no caller, so every milestone ever created was still
              open — the one on the status line included. A commitment
              you cannot mark met is not a commitment. */}
          {mayManage && (
            <>
              {" · "}
              <button
                type="button"
                onClick={() => void completeIt.invoke(stream.nextMilestone!.id)}
                disabled={completeIt.pending}
                className="underline underline-offset-4 hover:text-ink disabled:opacity-50"
              >
                mark it met
              </button>
            </>
          )}
        </p>
      )}
      {stream.healthNote && (
        <p className="max-w-[62ch] text-secondary sm:col-span-2">
          {stream.healthNote}
        </p>
      )}
      {completeIt.refusal && (
        <p className="border-l-2 border-danger pl-4 text-ink sm:col-span-2">
          {completeIt.refusal.message}
        </p>
      )}
    </div>
  );
}

function EditStream({
  stream,
  assignable,
  onDone,
  onCancel,
}: {
  stream: WorkStream;
  assignable: TeamMember[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(stream.name);
  const [intent, setIntent] = useState(stream.intent ?? "");
  const [lead, setLead] = useState(stream.leadUserId ?? "");
  const [health, setHealth] = useState(stream.health);
  const [note, setNote] = useState(stream.healthNote ?? "");

  const save = useAction(async () => {
    await api.patch(`/api/workstreams/${stream.id}`, {
      name,
      intent: intent.trim() || null,
      leadUserId: lead || null,
      health,
      healthNote: note.trim() || null,
    });
    onDone();
  });

  return (
    <div className="mt-4 grid max-w-3xl gap-4 border-l-2 border-line pl-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="sm:col-span-2">
        <TextArea
          label="The question this stream answers"
          hint="State the question or outcome that keeps this workstream focused."
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
        />
      </div>
      <Select label="Led by" value={lead} onChange={(e) => setLead(e.target.value)}>
        <option value="">Nobody yet</option>
        {assignable.map((m) => (
          <option key={`${m.userId}-${m.assignmentId ?? ""}`} value={m.userId}>
            {m.fullName}
          </option>
        ))}
      </Select>
      <Select
        label="Health"
        hint="Your judgement, not a calculation."
        value={health}
        onChange={(e) => setHealth(e.target.value)}
      >
        <option value="on_track">On track</option>
        <option value="at_risk">At risk</option>
        <option value="off_track">Off track</option>
      </Select>
      <div className="sm:col-span-2">
        <Field
          label="What it needs, or why it slipped"
          hint="One line. This is the weekly status."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div className="flex gap-3 sm:col-span-2">
        <Button
          tone="primary"
          loading={save.pending}
          disabled={!name.trim()}
          onClick={() => void save.invoke()}
        >
          Save
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {save.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-2">
          {save.refusal.message}
        </p>
      )}
    </div>
  );
}

const NEXT: Record<string, { to: string; label: string }> = {
  open: { to: "in_progress", label: "Start" },
  in_progress: { to: "done", label: "Done" },
  blocked: { to: "in_progress", label: "Unblock" },
  done: { to: "open", label: "Reopen" },
};

function TaskRow({
  task,
  parts,
  assignable,
  onChanged,
}: {
  task: WorkTask;
  parts?: EngagementParts;
  assignable: TeamMember[];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);

  const move = useAction(async (status: string) => {
    await api.patch(`/api/tasks/${task.id}`, { status });
    onChanged();
  });

  const next = NEXT[task.status];
  const done = task.status === "done";

  return (
    <div className="border-b border-line py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-base ${done ? "text-secondary line-through" : "text-ink"}`}>
            {task.title}
          </p>
          {task.body && (
            <p className="mt-1 max-w-[62ch] whitespace-pre-line text-sm text-secondary">
              {task.body}
            </p>
          )}
          <p className="mt-1 text-sm text-secondary">
            {task.assignee ?? "Unassigned"}
            {task.milestone && <span> · {task.milestone}</span>}
            {task.dueOn && (
              <span className={task.overdue ? "text-danger" : undefined}>
                {" · "}
                {task.overdue ? "overdue " : "due "}
                {day(task.dueOn)}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3 text-sm">
          <span className="label">{task.status.replace("_", " ")}</span>
          {/* mayMove and mayEdit are different answers to different
              questions, and the server gives both. An assignee moves their
              own task; only a manager rewrites one. */}
          {task.mayMove && next && (
            <Button
              tone={next.to === "done" ? "primary" : "quiet"}
              loading={move.pending}
              onClick={() => void move.invoke(next.to)}
            >
              {next.label}
            </Button>
          )}
          {task.mayMove && task.status !== "blocked" && !done && (
            <Button
              tone="quiet"
              loading={move.pending}
              onClick={() => void move.invoke("blocked")}
            >
              Blocked
            </Button>
          )}
          {task.mayEdit && (
            <Button tone="quiet" onClick={() => setEditing((v) => !v)}>
              {editing ? "Close" : "Edit"}
            </Button>
          )}
        </div>
      </div>

      {editing && task.mayEdit && (
        <EditTask
          task={task}
          parts={parts}
          assignable={assignable}
          onDone={() => {
            setEditing(false);
            onChanged();
          }}
          onCancel={() => setEditing(false)}
        />
      )}

      {move.refusal && (
        <p className="mt-3 border-l-2 border-danger pl-4 text-sm text-ink">
          {move.refusal.message}
        </p>
      )}
    </div>
  );
}

function EditTask({
  task,
  parts,
  assignable,
  onDone,
  onCancel,
}: {
  task: WorkTask;
  parts?: EngagementParts;
  assignable: TeamMember[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [body, setBody] = useState(task.body ?? "");
  const [assignee, setAssignee] = useState(task.assigneeId ?? "");
  const [milestone, setMilestone] = useState(task.milestoneId ?? "");
  const [due, setDue] = useState(task.dueOn ?? "");
  /**
   * Empty means "keep it where it is", which is why this does not start
   * from the task's current stream. The route distinguishes an absent key
   * from a null, so only an actual choice is sent — otherwise opening the
   * editor and saving would move an unfiled task nowhere, or worse, file
   * it under whatever happened to be first in the list.
   */
  const [moveTo, setMoveTo] = useState("");

  const save = useAction(async () => {
    await api.patch(`/api/tasks/${task.id}/details`, {
      title: title.trim(),
      body: body.trim() || null,
      // null is "unassign", an absent key is "leave it alone" — the route
      // distinguishes them, so an empty select must send null, not omit.
      assigneeId: assignee || null,
      milestoneId: milestone || null,
      dueOn: due || null,
      ...(moveTo ? { workstreamId: moveTo } : {}),
    });
    onDone();
  });

  return (
    <div className="mt-4 grid max-w-3xl gap-4 border-l-2 border-line pl-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field
          label="Task"
          value={title}
          maxLength={300}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="sm:col-span-2">
        <TextArea
          label="Detail"
          hint="Optional. What this involves, or what done looks like."
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      <Select label="Who" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
        <option value="">Unassigned</option>
        {assignable.map((m) => (
          <option key={`${m.userId}-${m.assignmentId ?? ""}`} value={m.userId}>
            {m.fullName}
          </option>
        ))}
      </Select>
      <Field
        label="Due"
        type="date"
        value={due}
        onChange={(e) => setDue(e.target.value)}
      />
      <Select
        label="Move to workstream"
        hint="Leave blank to keep it where it is."
        value={moveTo}
        onChange={(e) => setMoveTo(e.target.value)}
      >
        <option value="">Keep where it is</option>
        {(parts?.workstreams ?? []).map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </Select>
      <Select
        label="Towards which milestone"
        value={milestone}
        onChange={(e) => setMilestone(e.target.value)}
      >
        <option value="">No milestone</option>
        {(parts?.milestones ?? []).map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </Select>
      <div className="flex gap-3 sm:col-span-2">
        <Button
          tone="primary"
          loading={save.pending}
          disabled={!title.trim()}
          onClick={() => void save.invoke()}
        >
          Save
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {save.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-2">
          {save.refusal.message}
        </p>
      )}
    </div>
  );
}

function AddTask({
  engagementId,
  workstreamId,
  parts,
  assignable,
  onDone,
  onCancel,
}: {
  engagementId: string;
  workstreamId: string | null;
  parts?: EngagementParts;
  assignable: TeamMember[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const [milestone, setMilestone] = useState("");
  const [due, setDue] = useState("");

  const create = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/tasks`, {
      title: title.trim(),
      workstreamId: workstreamId ?? undefined,
      assigneeId: assignee || undefined,
      milestoneId: milestone || undefined,
      dueOn: due || undefined,
    });
    setTitle("");
    setAssignee("");
    setMilestone("");
    setDue("");
    onDone();
  });

  const submit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void create.invoke();
    },
    [create],
  );

  // A milestone from a different workstream is not a sensible parent.
  const milestones = (parts?.milestones ?? []).filter(
    (m) => m.workstreamId === workstreamId || m.workstreamId === null,
  );

  return (
    <form onSubmit={submit} className="mt-4 grid max-w-3xl gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field
          label="Task"
          value={title}
          maxLength={300}
          autoComplete="off"
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <Select label="Who" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
        <option value="">Unassigned</option>
        {assignable.map((m) => (
          <option key={`${m.userId}-${m.assignmentId ?? ""}`} value={m.userId}>
            {m.fullName}
          </option>
        ))}
      </Select>

      <Field
        label="Due"
        type="date"
        value={due}
        onChange={(e) => setDue(e.target.value)}
      />

      {milestones.length > 0 && (
        <div className="sm:col-span-2">
          <Select
            label="Towards which milestone"
            value={milestone}
            onChange={(e) => setMilestone(e.target.value)}
          >
            <option value="">No milestone</option>
            {milestones.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </div>
      )}

      <div className="flex gap-3 sm:col-span-2">
        <Button
          tone="primary"
          type="submit"
          loading={create.pending}
          disabled={!title.trim()}
        >
          Add it
        </Button>
        <Button tone="quiet" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {create.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-2">
          {create.refusal.message}
        </p>
      )}
    </form>
  );
}

// --------------------------------------------------------- deliverables ----
//
// Five of the six deliverable routes had no caller anywhere in the
// interface. The platform could receive a deliverable, decide on it and
// publish it to a client, and could not create one — the output consulting
// work exists to produce had no way into the system. This is that end.
//
// It lives under the workstream that produces it rather than on a screen of
// its own, because that is how consulting practice organises it: the stream
// owns its scope, its timeline and its outputs.

/** A file already in the engagement's drive, offered as a version. */
export interface FileChoice {
  versionId: string;
  label: string;
  folder: string;
}

const DELIVERABLE_TONE: Record<string, "rest" | "live" | "warning" | "danger"> = {
  draft: "rest",
  submitted: "warning",
  in_review: "warning",
  revision_requested: "danger",
  approved: "live",
  withdrawn: "rest",
};

function Deliverable({
  deliverable: d,
  engagementId,
  mayManage,
  files,
  onChanged,
}: {
  deliverable: StreamOutput;
  /** Sharing is an engagement-scoped act, so the route needs it. */
  engagementId: string;
  mayManage: boolean;
  files: FileChoice[];
  onChanged: () => void;
}) {
  const [panel, setPanel] = useState<"version" | "history" | "changes" | null>(null);

  const send = useAction(async () => {
    await api.post(`/api/deliverables/${d.id}/send-for-approval`, {
      versionId: d.latestVersionId,
    });
    onChanged();
  });

  const shareIt = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/share`, {
      type: "deliverable",
      objectId: d.id,
    });
    onChanged();
  });

  const withdrawIt = useAction(async () => {
    await api.post(`/api/deliverables/${d.id}/withdraw`, {});
    onChanged();
  });

  const tone = DELIVERABLE_TONE[d.status] ?? "rest";

  return (
    <div className="mt-3 border-l-2 border-line pl-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-base text-ink">{d.title}</p>
          <p className="mt-0.5 text-sm text-secondary">
            {d.latestVersionNo === null ? (
              "No version submitted yet"
            ) : (
              <>
                <span className="font-mono">v{d.latestVersionNo}</span>
                {d.submittedBy && ` · ${d.submittedBy}`}
                {d.submittedAt && ` · ${stamp(d.submittedAt)}`}
              </>
            )}
            {/* Named separately because it is a different fact. The newest
                version is not the approved one, and often is not. */}
            {d.approvedVersionNo !== null && (
              <span> · v{d.approvedVersionNo} approved</span>
            )}
            {d.awaitingClient && (
              <span className="text-ink"> · with the client for sign-off</span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Mark tone={tone}>{d.status.replace(/_/g, " ")}</Mark>
          {/* `dv_insert` (004) is reach > 0 AND submitted_by = app.uid()
              AND within the ceiling AND not a client. Submitting the work
              you did is the one thing a consultant most needs here, and
              it was hidden from them. */}
          <Button
            tone="quiet"
            onClick={() => setPanel((p) => (p === "version" ? null : "version"))}
          >
            New version
          </Button>
          {d.versions > 0 && (
            <Button
              tone="quiet"
              onClick={() => setPanel((p) => (p === "history" ? null : "history"))}
            >
              History ({d.versions})
            </Button>
          )}
          {/* Sending needs a version to send. Offering it on a deliverable
              with none would be a button that is always refused. */}
          {/* Two different acts, and conflating them is how a client
              ends up asked to approve something, or shown something
              nobody meant to show them.

                Share         make it visible. Reclassify to client
                              visible and publish, in one act — the two
                              halves separately are how you get a
                              deliverable marked client visible that was
                              never released, or a publication the
                              classification keeps invisible.
                Ask to sign   visible AND awaiting their decision. */}
          {mayManage && d.latestVersionId && (
            <Button
              tone="quiet"
              loading={shareIt.pending}
              onClick={() => void shareIt.invoke()}
            >
              Share with client
            </Button>
          )}
          {mayManage && d.latestVersionId && !d.awaitingClient && (
            <Button
              tone="secondary"
              loading={send.pending}
              onClick={() => void send.invoke()}
            >
              Ask them to sign off
            </Button>
          )}
          {/* Withdrawal is a written fact, not a deletion: the row stays
              and the status says what happened. What was already
              published stays published — taking that back is its own
              act, and one click should not quietly retract something a
              client has read and relied on. */}
          {mayManage && d.status !== "withdrawn" && (
            <Button
              tone="quiet"
              loading={withdrawIt.pending}
              onClick={() => void withdrawIt.invoke()}
            >
              Withdraw
            </Button>
          )}
          {mayManage && d.latestVersionId && (
            <Button
              tone="quiet"
              onClick={() => setPanel((p) => (p === "changes" ? null : "changes"))}
            >
              Ask for changes
            </Button>
          )}
        </div>
      </div>

      {(send.refusal || shareIt.refusal || withdrawIt.refusal) && (
        <p className="mt-3 border-l-2 border-danger pl-4 text-sm text-ink">
          {(send.refusal ?? shareIt.refusal ?? withdrawIt.refusal)?.message}
        </p>
      )}

      {panel === "version" && (
        <NewVersion
          deliverableId={d.id}
          files={files}
          onDone={() => {
            setPanel(null);
            onChanged();
          }}
          onCancel={() => setPanel(null)}
        />
      )}

      {panel === "history" && <History deliverableId={d.id} />}

      {panel === "changes" && d.latestVersionId && (
        <AskForChanges
          deliverableId={d.id}
          versionId={d.latestVersionId}
          onDone={() => {
            setPanel(null);
            onChanged();
          }}
          onCancel={() => setPanel(null)}
        />
      )}
    </div>
  );
}

function AddDeliverable({
  engagementId,
  workstreamId,
  ceiling,
  onDone,
  onCancel,
}: {
  engagementId: string;
  workstreamId: string;
  /** The highest classification this actor may write here. */
  ceiling: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("");
  // The floor, so the initial value is always one the select offers.
  const [classification, setClassification] = useState("client_visible");

  const create = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/deliverables`, {
      title: title.trim(),
      workstreamId,
      classification,
    });
    onDone();
  });

  return (
    <div className="mt-3 grid max-w-2xl gap-4 border-l-2 border-line pl-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field
          label="What this stream produces"
          hint="A deck, a model, a recommendation — the thing the client receives."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      {/* Same fix as the Notes composer: a consultant may create a
          deliverable — deliverable_insert is reach-based — but their
          ceiling is `internal`, so offering Restricted produced a bare
          refusal naming nothing. */}
      <ClassificationSelect
        value={classification}
        ceiling={ceiling}
        hint="Client visible items may be released through the portal. Internal items stay with the firm."
        onChange={setClassification}
      />
      <div className="flex items-end gap-3">
        <Button
          tone="primary"
          loading={create.pending}
          disabled={!title.trim()}
          onClick={() => void create.invoke()}
        >
          Create it
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {create.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-2">
          {create.refusal.message}
        </p>
      )}
    </div>
  );
}

function NewVersion({
  deliverableId,
  files,
  onDone,
  onCancel,
}: {
  deliverableId: string;
  files: FileChoice[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [fileId, setFileId] = useState("");
  const [notes, setNotes] = useState("");

  const submit = useAction(async () => {
    await api.post(`/api/deliverables/${deliverableId}/versions`, {
      driveFileId: fileId,
      notes: notes.trim() || undefined,
    });
    onDone();
  });

  if (files.length === 0) {
    return (
      <p className="mt-3 max-w-[62ch] text-sm text-secondary">
        No files are available yet. Upload the file in Files, then return here
        to submit it as a version.
      </p>
    );
  }

  return (
    <div className="mt-3 grid max-w-2xl gap-4">
      <Select
        label="Which file"
        hint="This exact file becomes the submitted version. Upload later edits as a new version."
        value={fileId}
        onChange={(e) => setFileId(e.target.value)}
      >
        <option value="">Choose a file</option>
        {files.map((f) => (
          <option key={f.versionId} value={f.versionId}>
            {f.label}
          </option>
        ))}
      </Select>
      <TextArea
        label="Notes"
        hint="Optional. What changed since the last version."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="flex gap-3">
        <Button
          tone="primary"
          loading={submit.pending}
          disabled={!fileId}
          onClick={() => void submit.invoke()}
        >
          Submit this version
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {submit.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink">
          {submit.refusal.message}
        </p>
      )}
    </div>
  );
}

function History({ deliverableId }: { deliverableId: string }) {
  const versions = useResource<DeliverableVersionRecord[]>(
    () =>
      api.get<DeliverableVersionRecord[]>(
        `/api/deliverables/${deliverableId}/versions`,
      ),
    [deliverableId],
  );

  return (
    <div className="mt-3">
      {versions.resource.state === "loading" && <SkeletonRows rows={2} />}
      {versions.resource.state === "failed" && (
        <Failed
          message={versions.resource.message}
          correlationId={versions.resource.correlationId}
        />
      )}
      {versions.resource.state === "ready" && (
        <ul className="text-sm">
          {versions.resource.data.map((v) => (
            <li key={v.id} className="flex flex-wrap gap-x-3 py-1 text-secondary">
              <span className="font-mono text-ink">v{v.versionNo}</span>
              {/* The name is nullable on purpose: a submitted version is
                  immutable and its history must stay complete even when the
                  person who submitted it is no longer reachable. */}
              <span>{v.submittedByName ?? "Someone no longer listed"}</span>
              <span className="text-muted">{stamp(v.submittedAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AskForChanges({
  deliverableId,
  versionId,
  onDone,
  onCancel,
}: {
  deliverableId: string;
  versionId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [body, setBody] = useState("");

  const ask = useAction(async () => {
    await api.post(`/api/deliverables/${deliverableId}/revisions`, {
      versionId,
      body: body.trim(),
    });
    onDone();
  });

  return (
    <div className="mt-3 grid max-w-2xl gap-4">
      <TextArea
        label="What needs doing"
        hint="Describe the changes needed for the version shown above."
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <div className="flex gap-3">
        <Button
          tone="primary"
          loading={ask.pending}
          disabled={!body.trim()}
          onClick={() => void ask.invoke()}
        >
          Request the revision
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {ask.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink">
          {ask.refusal.message}
        </p>
      )}
    </div>
  );
}
